/**
 * @fileoverview Shared subscription-status snapshot — the single client-side
 * source of truth for which plan the signed-in user is on.
 *
 * The identity response stamps `currentUser.subscriptionTier` once at login
 * and never refreshes it, so it can only ever be a pre-snapshot fallback:
 * every plan-aware surface (header upgrade button, sidebar badge, profile
 * popup, /account-settings, /partner, analytics) prefers this snapshot, and
 * each of them previously ran its own `SubscriptionAPI.getStatus()` fetch and
 * collapsed the answer to a boolean. This module holds ONE fetch, hands out
 * the full payload, and announces it on `subscription:status` so passive
 * surfaces can re-render without fetching.
 *
 * Freshness: the snapshot is reused for `SNAPSHOT_TTL_MS`, so the several
 * bootstrap consumers share a single request while a later visit to a surface
 * (opening the profile popup, landing on /partner) still picks up a plan change
 * made elsewhere. `subscription:activated` marks it stale immediately.
 *
 * Legacy tolerance: payloads cached before status payload v3 may lack `plan`
 * or the `partnerRate` block. Every derivation here falls back to
 * `isSubscribed` and never throws on a missing field.
 *
 * @module core/subscriptionStatus
 */

import { SubscriptionAPI } from './subscriptionApi.js';
import { eventBus } from './utils.js';

/** How long a fetched snapshot is served without re-requesting. */
const SNAPSHOT_TTL_MS = 60 * 1000;

/** @type {Object|null} Last successful status payload. */
let snapshot = null;
/** @type {number} Epoch ms of the last successful fetch (0 = stale/never). */
let fetchedAt = 0;
/** @type {Promise<Object|null>|null} In-flight request, shared by concurrent callers. */
let inFlight = null;

/**
 * The last fetched status payload, without triggering a fetch.
 * Surfaces that re-render on `subscription:status` read this synchronously.
 *
 * @returns {Object|null} Status payload, or null before the first fetch resolves
 */
export function getSubscriptionSnapshot() {
  return snapshot;
}

/**
 * Adopt a freshly-fetched status payload as the shared snapshot and announce it
 * on `subscription:status`.
 *
 * Exported for the billing pages (/upgrade, /subscription), which call
 * `SubscriptionAPI.getStatus()` directly because they need the failure envelope
 * that `fetchSubscriptionStatus` deliberately swallows — a 401 routes them to
 * login, other errors render an error card. Handing their payload here is what
 * keeps every passive surface (sidebar badge, header upgrade button, analytics)
 * in step after a visit to a billing page, without a second request.
 *
 * @param {Object|null} status - Status payload; falsy payloads are ignored
 * @returns {Object|null} The stored snapshot
 */
export function publishSubscriptionStatus(status) {
  if (!status) return snapshot;
  snapshot = status;
  fetchedAt = Date.now();
  eventBus.emit('subscription:status', status);
  return status;
}

/**
 * Fetch the subscription status, reusing a fresh snapshot and coalescing
 * concurrent callers into one request. Emits `subscription:status` with the
 * payload on every successful fetch.
 *
 * Never rejects: a failed fetch resolves to the stale snapshot (or null) so
 * callers can keep their last-known-good rendering.
 *
 * @param {Object} [options]
 * @param {boolean} [options.force=false] - Bypass the TTL and refetch
 * @returns {Promise<Object|null>} Status payload, or null when unavailable
 */
export function fetchSubscriptionStatus({ force = false } = {}) {
  if (!force && snapshot && (Date.now() - fetchedAt) < SNAPSHOT_TTL_MS) {
    return Promise.resolve(snapshot);
  }
  if (inFlight && !force) return inFlight;

  const request = (async () => {
    try {
      const result = await SubscriptionAPI.getStatus();
      return publishSubscriptionStatus(result?.success ? result.data : null);
    } catch (err) {
      console.warn('[subscriptionStatus] Failed to fetch status:', err?.message || err);
      return snapshot;
    }
  })();

  inFlight = request;
  request.finally(() => {
    if (inFlight === request) inFlight = null;
  });
  return request;
}

/**
 * Mark the snapshot stale so the next `fetchSubscriptionStatus` re-requests.
 * The payload itself is retained by default — surfaces keep rendering the last
 * known plan instead of flashing back to Basic while the refetch runs.
 *
 * @param {Object} [options]
 * @param {boolean} [options.clearSnapshot=false] - Also drop the retained payload
 */
export function invalidateSubscriptionStatus({ clearSnapshot = false } = {}) {
  fetchedAt = 0;
  if (clearSnapshot) snapshot = null;
}

// A completed checkout changes the plan without any surface refetching, so the
// activation signal (emitted by the router's post-checkout poll) retires the
// snapshot's freshness immediately.
eventBus.on('subscription:activated', () => invalidateSubscriptionStatus());

/**
 * The winning plan for a status payload.
 *
 * @param {Object|null} [status] - Status payload
 * @returns {'basic'|'premium'} Plan discriminator
 */
export function planFromStatus(status) {
  const plan = status?.plan;
  if (plan === 'basic' || plan === 'premium') return plan;
  // Pre-v3 payload: only the boolean existed.
  return status?.isSubscribed ? 'premium' : 'basic';
}

/**
 * The partner-rate block for a status payload: whether the 25% partner
 * discount is applied to the user's own subscription, and the discounted
 * monthly amount (null when the rate isn't configured).
 *
 * Null-guards a missing block (payloads cached before v3) so every surface can
 * read the shape unconditionally.
 *
 * @param {Object|null} [status] - Status payload
 * @returns {{applied: boolean, amount: number|null}}
 */
export function partnerRateFromStatus(status) {
  const block = status?.partnerRate;
  return {
    applied: block?.applied === true,
    amount: typeof block?.amount === 'number' ? block.amount : null
  };
}

/**
 * Whether a plan is a paid one (no upgrade affordances belong on it).
 *
 * @param {string} plan - Plan discriminator
 * @returns {boolean} True for premium
 */
export function isPaidPlan(plan) {
  return plan === 'premium';
}

/**
 * Display name for a plan. Callers compose the surrounding wording so each
 * surface keeps its own casing ("Premium plan", "PREMIUM PLAN", "Premium Plan").
 *
 * @param {string} plan - Plan discriminator
 * @returns {'Basic'|'Premium'} Human-readable plan name
 */
export function planName(plan) {
  return plan === 'premium' ? 'Premium' : 'Basic';
}

/**
 * GA4 `subscription_tier` user property for a status payload.
 *
 * Basic deliberately reports `'free'`, not `'basic'`: the property has years of
 * history under that value and renaming it would split every funnel.
 *
 * @param {Object|null} [status] - Status payload
 * @returns {'free'|'premium'} Analytics tier value
 */
export function analyticsTierFromStatus(status) {
  return planFromStatus(status) === 'premium' ? 'premium' : 'free';
}
